/**
 * The studio's idea of "who is signed in", in PCake.
 *
 * CreativeStudio has its own optional Supabase sign-in. In PCake everyone in the studio is
 * already signed in to PCake (Firebase), so this module answers with that account and turns
 * the studio's own sign-in off: isCloudAuthEnabled() is false, which hides its sign-in box and
 * account button, and getAuth() is Firebase's, so every `getAuth().currentUser.uid` in the
 * editor is the PCake user.
 *
 * The role decides two things the editor asks about: whether the library is the shared one
 * (Admin/Super see istudio/ and everyone's folder under it) and, in ImageStudio itself, which
 * tools are shown. ImageStudio sets it from its userRole prop; the cached role is the fallback.
 */
import { getAuth as getFirebaseAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirebase } from '../../services/firebase';

let studioRole = '';
export const setStudioRole = (role) => { studioRole = String(role || ''); };
const currentRole = () => {
  if (studioRole) return studioRole;
  try { return localStorage.getItem('userRole') || ''; } catch { return ''; }
};

const firebaseAuth = () => {
  try { return getFirebase().auth || getFirebaseAuth(); } catch { return getFirebaseAuth(); }
};

/** PCake has no separate studio sign-in. */
export const isCloudAuthEnabled = () => false;
export const isCloudAuthReady = () => true;

export const getAuth = () => firebaseAuth();
export const getCloudUser = () => firebaseAuth().currentUser || null;
export const getCloudRole = () => currentRole();
export const isCloudAdmin = () => ['Admin', 'Super'].includes(currentRole());

/** Resolves once Firebase has restored the session, so a uid-based path is never built early. */
export const cloudAuthReady = async () => {
  const auth = firebaseAuth();
  try { if (typeof auth.authStateReady === 'function') await auth.authStateReady(); } catch { /* fine */ }
};
export const initCloudAuth = () => cloudAuthReady();

/** Same contract as CreativeStudio's: fires with { user } (or null), returns an unsubscribe. */
export const onAuthChange = (fn) => onAuthStateChanged(firebaseAuth(), (user) => {
  try { fn(user ? { user: { id: user.uid, email: user.email } } : null); } catch { /* listener */ }
});

// The studio's own sign-in / sign-up / sign-out are never shown in PCake (see above).
export const signUp = async () => { throw new Error('Sign in to PCake to use the studio.'); };
export const signIn = async () => { throw new Error('Sign in to PCake to use the studio.'); };
export const signOut = async () => { /* PCake's own sign-out lives in Profile */ };
